import {
  AnalyticsProps,
  NOT_TRACKED_CONTENT,
} from '../../../InternalLibrary/analytics';
import { padStart } from 'lodash';
import classes from 'classnames';
import FieldGroup, {
  FieldGroupProps,
} from 'components/atoms/FieldGroup/FieldGroup';
import React, { memo, useMemo, useState } from 'react';
import Select, { SelectOption } from 'components/atoms/Select/Select';
import styles from './DateSelector.module.scss';
import TextInputEvent from '../../../hooks/eventCollector/events/TextInputEvent';
import useEventCollector from '../../../hooks/eventCollector/useEventCollector';

export type Props = AnalyticsProps & {
  onChange: (date: Date | null) => void;
  defaultDate?: Date;
  correctInvalidDays: boolean;
  daysLabel?: string;
  monthsLabel?: string;
  yearsLabel?: string;
  helperText?: string;
  validationText?: string;
  fieldGroupProps?: Partial<FieldGroupProps>;
  inputClassName?: string;
  ageRange?: { min: number; max: number };
};

export default memo(function DateSelector({
  onChange,
  correctInvalidDays = false,
  defaultDate,
  daysLabel = 'DD',
  monthsLabel = 'MM',
  yearsLabel = 'YYYY',
  helperText,
  validationText,
  fieldGroupProps,
  inputClassName,
  ageRange,
  analytics,
}: Props) {
  const { track } = useEventCollector();
  const daysOptions = useMemo(() => generateOptions(1, 31), []);
  const monthOptions = useMemo(() => generateOptions(1, 12), []);
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return generateOptions(
      ageRange ? currentYear - ageRange.max : 1900,
      ageRange ? currentYear - ageRange.min : currentYear,
    ).reverse();
  }, [ageRange]);

  // Short months, 1 based. 1 = January, 2 = Ferbruary, etc.
  const shortMonths = useMemo(() => [2, 4, 6, 9, 11], []);

  const [day, setDay] = useState<number | undefined>(defaultDate?.getDate());
  const [month, setMonth] = useState<number | undefined>(
    defaultDate ? defaultDate.getMonth() + 1 : undefined,
  );
  const [year, setYear] = useState<number | undefined>(
    defaultDate?.getFullYear(),
  );

  const handleChange = (value: string, field: 'day' | 'month' | 'year') => {
    const num = +value;

    const selected = {
      day,
      month,
      year,
      [field]: num,
    };

    switch (field) {
      case 'day':
        setDay((_) => num);
        break;
      case 'month':
        setMonth(num);
        break;
      case 'year':
        setYear(num);
        break;
    }

    if (!selected.day || !selected.month || !selected.year) {
      onChange(null);
      return;
    }

    let newDayValue = selected.day;

    // Invalid date correction logic
    if (correctInvalidDays) {
      if (
        selected.day === 31 &&
        selected.month !== 2 &&
        shortMonths.includes(selected.month)
      ) {
        newDayValue = 30;
      }
      if (selected.month === 2 && selected.day > 28) {
        newDayValue = 29;

        // Non-leap year
        if (new Date(selected.year, 1, newDayValue).getMonth() !== 1) {
          newDayValue = 28;
        }
      }
      if (newDayValue !== selected.day) {
        setDay((_) => newDayValue);
      }
    }

    const date = new Date(selected.year, selected.month - 1, newDayValue);
    onChange(date);
    if (analytics?.dontTrack) {
      return;
    }
    track(
      new TextInputEvent({
        description: analytics?.description,
        value: analytics?.dontTrackContent
          ? NOT_TRACKED_CONTENT
          : `${selected.year}-${padStart(
              selected.month.toString(),
              2,
              '0',
            )}-${padStart(newDayValue.toString(), 2, '0')}`,
      }),
    );
  };

  return (
    <FieldGroup
      direction="horizontal"
      helperText={helperText}
      {...fieldGroupProps}
    >
      <Select
        name="day"
        label={daysLabel}
        options={daysOptions}
        value={day}
        onChange={(e) => {
          handleChange(e.target.value, 'day');
        }}
        className={classes(
          styles.select,
          styles['select-left'],
          !!validationText && styles['select-error'],
          inputClassName,
        )}
        dataTest="xpath-Day"
        error={!!validationText}
      />
      <Select
        name="month"
        label={monthsLabel}
        options={monthOptions}
        value={month}
        onChange={(e) => {
          handleChange(e.target.value, 'month');
        }}
        className={classes(
          styles.select,
          styles['select-center'],
          !!validationText && styles['select-error'],
          inputClassName,
        )}
        dataTest="xpath-Month"
        error={!!validationText}
      />
      <Select
        name="year"
        label={yearsLabel}
        options={yearOptions}
        value={year}
        onChange={(e) => {
          handleChange(e.target.value, 'year');
        }}
        className={classes(
          styles.select,
          styles['select-right'],
          !!validationText && styles['select-error'],
          inputClassName,
        )}
        dataTest="xpath-Year"
        error={!!validationText}
      />
    </FieldGroup>
  );
});

function generateOptions(start: number, end: number) {
  const options: SelectOption[] = [];
  for (let i = start; i <= end; i++) {
    options.push({
      value: i,
      name: i.toString().padStart(2, '0'),
    });
  }
  return options;
}
